import { IProductHelperStrategy } from './product-helper-strategy.interface';
import {
  PRODUCT_SALE_STATUS,
  ProductCardViewData,
  ProductSaleStatusType,
} from '@services/MarppleShop/features/ProductList/type';
import { PeriodLimitedData, QuantityLimitedData, RemoveNull, SoldOutData, OutOfStockData } from '../types';
import { isBoolean, isDate } from '@fxts/core';

/**
 * - service: MarppleShop
 * - good_type_id: MPS
 */
export class ProductHelperStrategyV1 implements IProductHelperStrategy {
  /**
   * 한정판 여부
   */
  isLimited(data: QuantityLimitedData & PeriodLimitedData): boolean {
    return this.isQuantityLimited(data) || this.isPeriodLimited(data);
  }

  /**
   * 수량한정 상품인지 확인하는 함수
   * 수량이 0보다 크고 무제한이 아닌 경우
   */
  isQuantityLimited(data: QuantityLimitedData) {
    return isFinite(data.quantity) && data.quantity > 0;
  }

  /**
   * 판매 종료일이 설정된 상품인지 확인
   */
  isPeriodLimited(data: PeriodLimitedData): data is RemoveNull<PeriodLimitedData> {
    return Boolean(data.sell_end_at);
  }

  /**
   * 판매 시작 전인지 확인
   */
  isBeforeSale(sell_start_at: Date | null): sell_start_at is Date {
    return isDate(sell_start_at) && new Date(sell_start_at) > new Date();
  }

  /**
   * 판매 종료일 지남
   */
  isEndedSale(data: PeriodLimitedData): data is RemoveNull<PeriodLimitedData> {
    // 종료일이 설정되지 않은 경우
    if (!this.isPeriodLimited(data)) {
      return false;
    }
    // 현재 시간이 종료일 이후인지 확인
    return new Date() > new Date(data.sell_end_at);
  }

  /**
   * 한정 수량이 다 팔림
   */
  isLimitedQuantitySoldOut(data: OutOfStockData): boolean {
    return this.isQuantityLimited(data) && data.order_count >= data.quantity;
  }

  /**
   * 품절인지 확인하는 함수, 아래 조건 OR
   * - 1. 재고 수량 관리가 다 팔린 경우
   * - 2. "수량한정"일 때 잔여수량이 없는 경우
   * - 3. 종료일이 설정되어 있는데, 종료일이 지난 경우
   */
  isSoldOut(data: SoldOutData): boolean {
    return data.out_of_stock || this.isLimitedQuantitySoldOut(data) || this.isEndedSale(data);
  }

  /**
   * 판매 상태를 확인하는 함수
   */
  getSaleStatus(data: Pick<ProductCardViewData, 'sell_start_at' | 'sell_end_at'>): ProductSaleStatusType {
    const now = new Date();
    const end_at = data.sell_end_at ? new Date(data.sell_end_at) : null;

    if (this.isBeforeSale(data.sell_start_at)) {
      return PRODUCT_SALE_STATUS.BEFORE_SALE;
    }

    if (end_at) {
      if (now > end_at) {
        return PRODUCT_SALE_STATUS.ENDED_SALE;
      } else {
        return PRODUCT_SALE_STATUS.LIMITED_SALE;
      }
    }

    return PRODUCT_SALE_STATUS.ON_SALE;
  }

  /**
   * 인기 상품인지 확인
   */
  isPopular(is_popular: boolean | undefined): is_popular is true {
    return isBoolean(is_popular) && is_popular;
  }

  /**
   * 급상승 상품인지 확인
   */
  isTrending(is_trending: boolean | undefined): is_trending is true {
    return isBoolean(is_trending) && is_trending;
  }

  /**
   * 멤버십 상품인지 확인
   */
  isMembership(is_membership: boolean): is_membership is true {
    return isBoolean(is_membership) && is_membership;
  }

  /**
   * 브랜드딜 상품인지 확인
   */
  isBrandDeal(is_brand_deal: boolean | undefined): is_brand_deal is true {
    return isBoolean(is_brand_deal) && is_brand_deal;
  }

  /**
   * 펀딩 상품인지 확인
   */
  isFunding(is_funding: boolean | undefined): is_funding is true {
    return isBoolean(is_funding) && is_funding;
  }
}
